/**
 * AppointmentSlideOver — right-side preview panel rendered 1:1 from
 *   /Users/gudupuramesh/Documents/design ui/anti Interior io ui web/app.js
 *   (the `openDetails('appt')` body template, lines 258-294).
 *
 * Layout (matches the folder verbatim):
 *   Header  — "Appointment Details" + `×` close (NO Edit button here)
 *   Body
 *     1. Eyebrow (purpose, UPPERCASE) · Title (h3 big) · Subtitle ("Client / Lead")
 *     2. Action row — Edit (primary) + Cancel (secondary, red text)
 *     3. Card with 4 rows: Date & Time · Location Mode · Address / Link · Assigned Staff
 *     4. AGENDA & NOTES section (note text on light grey card)
 *     5. Delete link (subtle, bottom)
 *
 * Real data: `useAppointment(apptId)` live snapshot.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  deleteAppointment,
  updateAppointment,
} from '@/lib/appointments';
import {
  locationModeLabel,
  meetingPurposeLabel,
  useAppointment,
  type AppointmentItem,
  type AppointmentType,
  type LocationMode,
  type MeetingPurpose,
} from '@/lib/useAppointments';

export type AppointmentSlideOverProps = {
  apptId: string | null;
  onClose: () => void;
};

// ───── Helpers (fallback from legacy `type` field) ─────

function legacyModeFor(t: AppointmentType): LocationMode {
  switch (t) {
    case 'site_visit':     return 'site';
    case 'office_meeting': return 'office';
    case 'virtual_call':   return 'online';
    case 'other':          return 'other';
  }
}

function legacyPurposeFor(t: AppointmentType): MeetingPurpose {
  switch (t) {
    case 'site_visit':     return 'site_visit';
    case 'office_meeting': return 'initial_consultation';
    case 'virtual_call':   return 'initial_consultation';
    case 'other':          return 'other';
  }
}

function purposeFor(a: AppointmentItem): MeetingPurpose {
  return a.meetingPurpose ?? legacyPurposeFor(a.type);
}

function modeFor(a: AppointmentItem): LocationMode {
  return a.locationMode ?? legacyModeFor(a.type);
}

function dateLabel(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function timeRange(a: AppointmentItem): string {
  const startMs = a.scheduledAt?.toMillis?.();
  if (!startMs) return '—';
  const start = new Date(startMs).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const mins = a.durationMins && a.durationMins > 0 ? a.durationMins : 60;
  const end = new Date(startMs + mins * 60 * 1000).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `${start} - ${end}`;
}

// ───── Component ─────

export function AppointmentSlideOver({ apptId, onClose }: AppointmentSlideOverProps) {
  const navigate = useNavigate();
  const open = apptId !== null;
  const { appointment, loading, error } = useAppointment(apptId ?? undefined);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Body scroll lock + Esc to close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const handleCancelAppt = async () => {
    if (!appointment) return;
    setBusy(true);
    try {
      await updateAppointment(appointment.id, { status: 'cancelled' });
    } catch (e) {
      console.warn('[AppointmentSlideOver] cancel failed', e);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!appointment) return;
    setBusy(true);
    try {
      await deleteAppointment(appointment.id);
      onClose();
    } catch (e) {
      console.warn('[AppointmentSlideOver] delete failed', e);
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={onClose}
        aria-hidden
      />
      <div
        className={`slide-over${open ? ' open' : ''}`}
        role="dialog"
        aria-label="Appointment details"
        aria-hidden={!open}
      >
        <div className="slide-header">
          <h2 className="slide-title">Appointment Details</h2>
          <button
            className="slide-close"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {!open ? null : loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading…
            </div>
          ) : error ? (
            <div
              className="card"
              style={{
                padding: 12,
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                borderColor: 'var(--critical)',
              }}
            >
              <i className="ph ph-warning-circle" /> {error}
            </div>
          ) : !appointment ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
              Appointment not found.
            </div>
          ) : (
            <>
              {/* 1 — Eyebrow + Title + Subtitle */}
              <div className="detail-section">
                <div
                  className="detail-label"
                  style={{
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: 11,
                  }}
                >
                  {meetingPurposeLabel(purposeFor(appointment))}
                </div>
                <div className="detail-text" style={{ fontWeight: 600, fontSize: 18 }}>
                  {appointment.title}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {appointment.clientName?.trim() || 'Client / Lead'}
                </div>
              </div>

              {/* 2 — Action row (Edit + Cancel) */}
              {appointment.status === 'scheduled' ? (
                <div className="detail-section" style={{ flexDirection: 'row', gap: 16 }}>
                  <button
                    className="btn-primary"
                    type="button"
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => navigate(`/calendar/${appointment.id}/edit`)}
                    disabled={busy}
                  >
                    <i className="ph ph-pencil-simple" /> Edit
                  </button>
                  <button
                    className="btn-secondary"
                    type="button"
                    style={{ flex: 1, justifyContent: 'center', color: 'var(--critical)' }}
                    onClick={handleCancelAppt}
                    disabled={busy}
                  >
                    <i className="ph ph-x" /> Cancel
                  </button>
                </div>
              ) : (
                <div className="detail-section" style={{ flexDirection: 'row', gap: 16 }}>
                  <button
                    className="btn-primary"
                    type="button"
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => navigate(`/calendar/${appointment.id}/edit`)}
                    disabled={busy}
                  >
                    <i className="ph ph-pencil-simple" /> Edit
                  </button>
                </div>
              )}

              {/* 3 — Card with 4 rows */}
              <div className="card" style={{ padding: 16, marginTop: 8 }}>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    <i className="ph ph-calendar" /> Date &amp; Time
                  </span>
                  <span style={{ fontWeight: 500, textAlign: 'right' }}>
                    {dateLabel(appointment.scheduledAt?.toMillis() ?? null)}
                    <br />
                    {timeRange(appointment)}
                  </span>
                </div>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    <i className="ph ph-map-pin" /> Location Mode
                  </span>
                  <span style={{ fontWeight: 500 }}>
                    {locationModeLabel(modeFor(appointment))}
                  </span>
                </div>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    <i className="ph ph-buildings" /> Address / Link
                  </span>
                  <span style={{ fontWeight: 500, textAlign: 'right' }}>
                    {appointment.location ?? '—'}
                  </span>
                </div>
                <div className="detail-row">
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    <i className="ph ph-user" /> Assigned Staff
                  </span>
                  <span style={{ fontWeight: 500 }}>
                    {appointment.assignedToName?.trim() ||
                      appointment.attendees?.[0] ||
                      'Unassigned'}
                  </span>
                </div>
              </div>

              {/* 4 — Agenda & Notes (light grey card) */}
              {appointment.notes?.trim() ? (
                <div className="detail-section">
                  <div className="detail-label">Agenda &amp; Notes</div>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'var(--text-primary)',
                      background: '#FAFAFA',
                      padding: 12,
                      borderRadius: 6,
                      border: '1px solid var(--border)',
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {appointment.notes}
                  </p>
                </div>
              ) : null}

              {/* Outcome — shown only for terminal statuses */}
              {appointment.outcome?.trim() ? (
                <div className="detail-section">
                  <div className="detail-label">Outcome</div>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'var(--text-primary)',
                      background: '#FAFAFA',
                      padding: 12,
                      borderRadius: 6,
                      border: '1px solid var(--border)',
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {appointment.outcome}
                  </p>
                </div>
              ) : null}

              {/* 5 — Delete link */}
              <div className="detail-section" style={{ marginTop: 16 }}>
                {confirmDelete ? (
                  <div
                    className="card"
                    style={{
                      padding: 12,
                      background: 'var(--critical-bg)',
                      borderColor: 'var(--critical)',
                    }}
                  >
                    <div style={{ fontSize: 13, marginBottom: 8 }}>
                      Permanently delete this appointment?
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn-secondary"
                        type="button"
                        style={{ flex: 1, justifyContent: 'center' }}
                        onClick={() => setConfirmDelete(false)}
                        disabled={busy}
                      >
                        Keep
                      </button>
                      <button
                        className="btn-primary"
                        type="button"
                        style={{
                          flex: 1,
                          justifyContent: 'center',
                          background: 'var(--critical)',
                          borderColor: 'var(--critical)',
                        }}
                        onClick={handleDelete}
                        disabled={busy}
                      >
                        <i className="ph ph-trash" /> Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    style={{
                      background: 'transparent',
                      border: 0,
                      color: 'var(--text-tertiary)',
                      fontSize: 12,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      alignSelf: 'flex-start',
                      padding: 0,
                    }}
                  >
                    Delete appointment
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
